<template>
    <div class="d-flex gap-4 flex-column">

        <Card :header="$t('currentAvailability')" :tooltip="$t('tooltip')" overlay :loading="loading">

            <template #nav>
                <div :key="spaceId" class="d-flex gap-3 flex-column">
                    <div class="d-flex gap-3">
                        <Multiselect
                            :value="filters.availabilities"
                            :fetch-options-function="fetchOfferAvailabilities"
                            :placeholder="$t('availabilityTime')"
                            :selected-options-label="$t('selectedAvailabilities')"
                            searchable
                            track-by="value"
                            label="label"
                            select-label=""
                            :disabled="loading"
                            show-checkboxes
                            show-selected
                            multiple
                            preselect-all
                            :close-on-select="false"
                            :clear-on-select="false"
                            :allow-empty="false"
                            class="width-200"
                            @changeTrackBy="filters.availabilities = $event"
                            @loading="setLoading('availabilities', $event)"
                        />
                        <MultiselectAvailableCampaigns :allow-empty="false" :value="filters.campaigns" :disabled="loading" @changeTrackBy="filters.campaigns = $event" @loading="setLoading('campaigns', $event)" />
                        <Multiselect
                            v-show="filters.showProductGroupsResults"
                            :value="filters.groups"
                            :fetch-options-function="fetchSpaceGroups"
                            :placeholder="$t('productGroup')"
                            :selected-options-label="$t('selectedGroups')"
                            searchable
                            track-by="id"
                            label="name"
                            select-label=""
                            :disabled="loading"
                            show-checkboxes
                            show-selected
                            multiple
                            preselect-all
                            :close-on-select="false"
                            :clear-on-select="false"
                            class="width-200"
                            @changeTrackBy="filters.groups = $event"
                            @loading="setLoading('groups', $event)"
                        />
                        <FormCategoryTreeSelect
                            v-show="!filters.showProductGroupsResults"
                            ref="FormCategoryTreeSelect"
                            :value="filters.categories"
                            :disabled="loading"
                            class="width-200"
                            preselect
                            prevent-empty-selection
                            :clearable="false"
                            @change="filters.categories = $event"
                            @loading="setLoading('categories', $event)"
                        />
                    </div>
                    <div class="d-flex gap-3 justify-content-end align-items-center">
                        <b-form-checkbox v-model="filters.showProductGroupsResults" switch :disabled="loading">{{ $t('showResultsForProductGroups') }}</b-form-checkbox>
                        <b-form-checkbox v-model="filters.showAvailabilityTime" switch :disabled="loading">{{ $t('showAvailabilityTime') }}</b-form-checkbox>
                    </div>
                </div>
            </template>

            <div class="dropdown-divider border-bottom border-light"></div>

            <div class="d-flex gap-4 flex-column">

                <CurrentProductsAvailability />

                <div class="dropdown-divider border-bottom border-light"></div>

                <CurrentUnavailableProducts />

            </div>

        </Card>

    </div>
</template>



<script>
    import FormCategoryTreeSelect from '@/components/common/FormCategoryTreeSelect.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import MultiselectAvailableCampaigns from '@/components/common/MultiselectAvailableCampaigns.vue';
    import { usePerSpaceFilters } from '@/mixins/usePerSpaceFilters';
    import CurrentProductsAvailability from './CurrentProductsAvailability.vue';
    import CurrentUnavailableProducts from './CurrentUnavailableProducts.vue';

    const DEFAULT_FILTERS = {
        showProductGroupsResults: false,
        showAvailabilityTime: false,
        campaigns: [],
        availabilities: [],
        groups: [],
        categories: [],
    };

    export default {
        name: 'CurrentAvailability',
        mixins: [
            usePerSpaceFilters(DEFAULT_FILTERS, {
                filtersPath: 'filters',
                loadingStatesPath: 'loadingStates',
                spaceIdPath: 'spaceId',
                apiParamsPath: 'apiParams',
                loadHandlerName: 'fetchData',
                abortHandlerName: 'abortRequest',
                resetHandlerName: 'resetData',
            }),
        ],
        components: {
            Multiselect,
            FormCategoryTreeSelect,
            CurrentProductsAvailability,
            CurrentUnavailableProducts,
            MultiselectAvailableCampaigns,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        provide() {
            return {
                parentComponent: this,
            };
        },
        data() {
            return {
                requestCurrentProductsAvailability: this.$req('space/CurrentProductsAvailability'),
                requestCurrentUnavailableProducts: this.$req('space/CurrentUnavailableProducts'),
                requestSpaceGroups: this.$req('producerProductGroups/SpaceGroups'),
                requestOfferAvailabilities: this.$req('offers/OfferAvailabilities'),
                filters: structuredClone(DEFAULT_FILTERS),
                loadingStates: {
                    availabilities: false,
                    campaigns: false,
                    groups: false,
                    categories: false,
                },
                availabilitiesMap: {},
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            loadingData() {
                return this.requestCurrentProductsAvailability.getLoading() || this.requestCurrentUnavailableProducts.getLoading();
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const requestParams = {};
                if (this.filters.campaigns?.length) requestParams.campaigns = [...this.filters.campaigns].sort().join(',');
                if (!this.filters.showProductGroupsResults && this.filters.categories?.length) requestParams.categories = [...this.filters.categories].sort().join(',');
                if (this.filters.showProductGroupsResults && this.filters.groups?.length) requestParams.groups = [...this.filters.groups].sort().join(',');
                return { urlParams, requestParams };
            },
        },
        methods: {
            abortRequest() {
                this.requestCurrentProductsAvailability.abortAll();
                this.requestCurrentUnavailableProducts.abortAll();
            },
            resetData() {
                this.requestCurrentProductsAvailability.resetAll();
                this.requestCurrentUnavailableProducts.resetAll();
            },
            fetchData() {
                const p1 = this.requestCurrentProductsAvailability.get(this.apiParams);
                const p2 = this.requestCurrentUnavailableProducts.get(this.apiParams);
                return Promise.all([p1, p2]);
            },
            fetchOfferAvailabilities() {
                return this.requestOfferAvailabilities.get().then(data => {
                    this.availabilitiesMap = data.reduce((acc, item) => ({ ...acc, [item.value]: item.label }), {});
                    return data;
                });
            },
            fetchSpaceGroups() {
                return this.requestSpaceGroups.get({ urlParams: { spaceId: this.spaceId } }).then(data => data.items);
            },
            // opcjonalnie: jeśli nadal potrzebujesz reakcji na zewnętrzne "resetFilters"
            resetFilters() {
                // przy użyciu mixiny lepiej zapisać defaults i wczytać je per space:
                this.resetPerSpaceDefaults();
                this.resetAllLoading();
            },
        },
        created() {
            this.parentComponent.$on('resetFilters', this.resetFilters);
        },
        beforeDestroy() {
            this.parentComponent.$off('resetFilters', this.resetFilters);
        },
    };
</script>



<i18n lang="json" locale="pl" scope="local">
{
    "tooltip": "Zestawienie prezentuje przegląd aktualnej dostępności w sklepach oraz dodatkowo listę niedostępnych produktów."
}
</i18n>

<i18n lang="json" locale="en" scope="local">
{
    "tooltip": "The list presents a review of current availability in stores and an additional list of inaccessible products."
}
</i18n>
